import { ToasterType } from '@clickcease/cc-client-components-lib/dist/types/ToasterTypes'
import Axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import useAppStoreModule, { TOASTS_TYPES } from 'Stores/app'
import useUserStoreModule from 'Stores/user'
import { getCookie } from 'typescript-cookie'

import i18n from 'Lib/i18n'
import { ApiError } from '../types/apiError'
import { createDefaultReturnProxy } from 'Utils/proxy'
import storage from 'Utils/storage'

const baseURL = process.env.REACT_APP_CC_CORE

function authRequestInterceptor(config: AxiosRequestConfig) {
  const token = getCookie('jwtToken') || storage.getToken()
  const cookieBeforeParse = getCookie('authCode')
  config.headers = config.headers ?? {}
  if (cookieBeforeParse) {
    const authCodeCookie = JSON.parse(cookieBeforeParse) as {
      authCode: string
      userId: number
    }
    //get auth code
    if (authCodeCookie) {
      config.headers.common['x-auth-code'] = authCodeCookie.authCode
    }
  }

  //get token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  config.headers.Accept = 'application/json'
  return config
}

const requestBaseURLInterceptor = (config: AxiosRequestConfig) => {
  if (!config.baseURL) config.baseURL = baseURL
  return config
}

const getAxiosInstance: (config?: AxiosRequestConfig) => AxiosInstance = (config) => {
  const axios = Axios.create(config)
  axios.interceptors.request.use(requestBaseURLInterceptor)
  axios.interceptors.request.use(authRequestInterceptor)
  axios.interceptors.response.use(
    (response) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return response.data
    },
    (error) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (error.response?.status === 401 || error.response?.status === 403) {
        useUserStoreModule.getState()?.logOut()
      }
      return Promise.reject(error)
    }
  )
  return axios
}

const axios = getAxiosInstance()

const translationsObject: Record<string, string> = i18n.t('apiErrors', {
  ns: 'errors',
  returnObjects: true,
})

const translationLookUp = createDefaultReturnProxy<string>(
  translationsObject,
  translationsObject['GENERIC_ERROR']
)

interface RequestConfig<R> extends AxiosRequestConfig<R> {
  hideErrorToaster?: boolean
  onSuccessToast?: {
    message: string
    type?: ToasterType
  }
}

export const request = async <T, R = any>(config: RequestConfig<R>): Promise<T | ApiError> => {
  // eslint-disable-next-line react-hooks/rules-of-hooks, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  const showToaster = useAppStoreModule.getState().showToaster
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { hideErrorToaster, onSuccessToast, baseURL, ...axiosConfig } = config
  try {
    const response = baseURL
      ? // according to Dudu Hayo - this needed for recorder. Pls address him if you want to change
        await getAxiosInstance({ baseURL })({ ...axiosConfig })
      : await axios({ ...axiosConfig })
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    config.onSuccessToast &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      showToaster(config.onSuccessToast.message, config.onSuccessToast.type ?? TOASTS_TYPES.SUCCESS)
    return response.data as T
  } catch (error) {
    const err = error as ApiError
    console.error('PM Dashboard', err)

    if (!config.hideErrorToaster && err?.response?.data?.message) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      showToaster(translationLookUp[err.response.data.message], TOASTS_TYPES.FAILURE)
    }
    return err
  }
}
